/**
 * RevokeApiKeyModal
 *
 * Confirmation modal for permanently deleting (revoking) an API Key.
 * Requires the user to type the key name to confirm the destructive action.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, AlertTriangle } from 'lucide-react';
import { ApiKey } from '../../../types/apiKey';
import { revokeApiKey } from '../../../services/apiKeyService';

interface RevokeApiKeyModalProps {
  apiKey: ApiKey;
  onClose: () => void;
  onSuccess: (revokedId: number) => void;
}

export const RevokeApiKeyModal: React.FC<RevokeApiKeyModalProps> = ({
  apiKey,
  onClose,
  onSuccess,
}) => {
  const [confirmName, setConfirmName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmed = confirmName === apiKey.name;

  const handleRevoke = async () => {
    if (!isConfirmed) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await revokeApiKey(apiKey.workspace_id, apiKey.id);
      onSuccess(apiKey.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao revogar chave');
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const content = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="bg-[#2a2a2a] border border-[#333333] rounded-xl w-full max-w-md mx-4 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#333333]">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 bg-red-500/20 rounded-lg">
              <Trash2 className="w-5 h-5 text-red-400" />
            </div>
            <h2 className="text-base font-semibold text-white">Revogar chave de API</h2>
          </div>
          {!isSubmitting && (
            <button
              onClick={onClose}
              className="p-1.5 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Warning */}
          <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-300 space-y-1">
              <p className="font-medium">Esta ação é irreversível.</p>
              <p>
                Todas as integrações que usam a chave{' '}
                <code className="bg-red-500/20 px-1 rounded font-mono">{apiKey.api_key}</code>{' '}
                perderão acesso imediatamente.
              </p>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Confirmation input */}
          <div>
            <label className="block text-sm text-[#cccccc] mb-1.5">
              Digite <strong className="text-white">{apiKey.name}</strong> para confirmar:
            </label>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={apiKey.name}
              className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-[#444444] rounded-lg text-white text-sm placeholder-[#666666] focus:outline-none focus:border-red-500 transition-colors"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isConfirmed && !isSubmitting) {
                  handleRevoke();
                }
              }}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm text-[#888888] hover:text-white transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleRevoke}
              disabled={!isConfirmed || isSubmitting}
              className="flex items-center gap-2 px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {isSubmitting ? 'Revogando...' : 'Revogar chave'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default RevokeApiKeyModal;
